/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  TunnelDiagnosticStep,
  TunnelDiagnosticStep$inboundSchema,
} from "./tunneldiagnosticstep.js";
import {
  TunnelHTTPProgress,
  TunnelHTTPProgress$inboundSchema,
} from "./tunnelhttpprogress.js";

/**
 * Diagnostic collection state
 */
export const TunnelDiagnosticsState = {
  Pending: "pending",
  Available: "available",
  Stale: "stale",
  Unavailable: "unavailable",
  Unsupported: "unsupported",
  Disabled: "disabled",
} as const;
/**
 * Diagnostic collection state
 */
export type TunnelDiagnosticsState = OpenEnum<typeof TunnelDiagnosticsState>;

/**
 * Reachable means transport only, not MCP success.
 */
export const TunnelDiagnosticsTargetState = {
  Pending: "pending",
  Reachable: "reachable",
  Unreachable: "unreachable",
  Unknown: "unknown",
} as const;
/**
 * Reachable means transport only, not MCP success.
 */
export type TunnelDiagnosticsTargetState = OpenEnum<
  typeof TunnelDiagnosticsTargetState
>;

export type TunnelDiagnostics = {
  /**
   * Consecutive failed transport probes
   */
  consecutiveFailures?: number | undefined;
  dns?: TunnelDiagnosticStep | undefined;
  httpProgress?: TunnelHTTPProgress | undefined;
  /**
   * HTTP response age at view time, -1 if absent
   */
  lastHttpResponseAgeMs?: number | undefined;
  /**
   * Last observed HTTP status, including authentication challenges
   */
  lastHttpStatus?: number | undefined;
  /**
   * Last transport failure category
   */
  lastTransportError?: string | undefined;
  /**
   * Transport failure age at view time, -1 if absent
   */
  lastTransportErrorAgeMs?: number | undefined;
  /**
   * Gateway receipt time
   */
  receivedAt?: Date | undefined;
  /**
   * HTTP attempts since agent process start
   */
  requestsTotal?: number | undefined;
  /**
   * Probe age at view time, -1 if never sampled
   */
  sampleAgeMs?: number | undefined;
  /**
   * Diagnostic collection state
   */
  state: TunnelDiagnosticsState;
  /**
   * Reachable means transport only, not MCP success.
   */
  targetState?: TunnelDiagnosticsTargetState | undefined;
  tcp?: TunnelDiagnosticStep | undefined;
  tls?: TunnelDiagnosticStep | undefined;
  /**
   * Transport errors since agent process start
   */
  transportErrorsTotal?: number | undefined;
};

/** @internal */
export const TunnelDiagnosticsState$inboundSchema: z.ZodMiniType<
  TunnelDiagnosticsState,
  unknown
> = openEnums.inboundSchema(TunnelDiagnosticsState);

/** @internal */
export const TunnelDiagnosticsTargetState$inboundSchema: z.ZodMiniType<
  TunnelDiagnosticsTargetState,
  unknown
> = openEnums.inboundSchema(TunnelDiagnosticsTargetState);

/** @internal */
export const TunnelDiagnostics$inboundSchema: z.ZodMiniType<
  TunnelDiagnostics,
  unknown
> = z.pipe(
  z.object({
    consecutive_failures: z.optional(z.int()),
    dns: z.optional(TunnelDiagnosticStep$inboundSchema),
    http_progress: z.optional(TunnelHTTPProgress$inboundSchema),
    last_http_response_age_ms: z.optional(z.int()),
    last_http_status: z.optional(z.int()),
    last_transport_error: z.optional(z.string()),
    last_transport_error_age_ms: z.optional(z.int()),
    received_at: z.optional(
      z.pipe(z.iso.datetime({ offset: true }), z.transform(v => new Date(v))),
    ),
    requests_total: z.optional(z.int()),
    sample_age_ms: z.optional(z.int()),
    state: TunnelDiagnosticsState$inboundSchema,
    target_state: z.optional(TunnelDiagnosticsTargetState$inboundSchema),
    tcp: z.optional(TunnelDiagnosticStep$inboundSchema),
    tls: z.optional(TunnelDiagnosticStep$inboundSchema),
    transport_errors_total: z.optional(z.int()),
  }),
  z.transform((v) => {
    return remap$(v, {
      "consecutive_failures": "consecutiveFailures",
      "http_progress": "httpProgress",
      "last_http_response_age_ms": "lastHttpResponseAgeMs",
      "last_http_status": "lastHttpStatus",
      "last_transport_error": "lastTransportError",
      "last_transport_error_age_ms": "lastTransportErrorAgeMs",
      "received_at": "receivedAt",
      "requests_total": "requestsTotal",
      "sample_age_ms": "sampleAgeMs",
      "target_state": "targetState",
      "transport_errors_total": "transportErrorsTotal",
    });
  }),
);

export function tunnelDiagnosticsFromJSON(
  jsonString: string,
): SafeParseResult<TunnelDiagnostics, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => TunnelDiagnostics$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'TunnelDiagnostics' from JSON`,
  );
}
